/**
 * AI Auto Mode status resolution — the header badge on the conversation screen (auto mode) used
 * to be a pure DEDUCTION from `messages[last].sender_type`: "last message from the customer" meant
 * "Analyzing Message", forever, with nothing to reset it. It never looked at whether the engine was
 * actually running, had finished, or had failed.
 *
 * Confirmed failure (2026-08-14): an engine run failed to send a form. Nothing about that failure
 * touches `messages` or `sender_type`, so the badge kept reading "Analyzing Message" 30+ minutes
 * after the run had already ended — the control could only ever say "yes, working on it".
 *
 * This module replaces the deduction with a resolution against `public.workflow_run` (the run
 * observability spine — see supabase/migrations/20260603190000_obs_spine_tables.sql), read via
 * `useLatestConversationRun`. Pure + independently testable: no Supabase/React here.
 *
 * Follow-up (2026-08-14): a conversation with zero messages — a fresh one, or one the operator just
 * reset via public.reset_tenant_conversation — has nothing to analyze, and now always resolves to
 * 'empty' regardless of what workflow_run says (see the comment on the `!hasMessages` branch below
 * for why that also has to override a stale run row, not just an absent one).
 */

export type AiAutoModeState = 'empty' | 'processing' | 'awaitingCustomer' | 'failed' | 'unknown';

/** Why we landed on 'unknown' — surfaced so the UI can say something more useful than a shrug. */
export type UnknownReason = 'queryFailed' | 'staleRunning' | 'noRunRecorded';

export interface AiAutoStatusDetail {
  /** Present for 'processing' and 'unknown'/'staleRunning' — when the run started. */
  startedAt?: string;
  /** Present for 'failed' and 'awaitingCustomer' — when the run ended, if it did. */
  finishedAt?: string | null;
  /** Present for 'failed' when the engine recorded one. */
  errorSummary?: string | null;
  /** Present for 'unknown'. */
  reason?: UnknownReason;
}

export interface AiAutoStatusResult {
  state: AiAutoModeState;
  detail: AiAutoStatusDetail;
}

/** The shape this module needs from a `workflow_run` row — a subset of WorkflowRun (./types). */
export interface LatestRunForStatus {
  status: string;
  started_at: string;
  finished_at: string | null;
  error_summary: string | null;
}

/**
 * A run past this age, still 'running', is treated as stuck rather than "processing" forever.
 *
 * Grounded in what the engine itself does, not a guess: a single node's HTTP call is capped at
 * `Math.min(nodeConfig.timeout || 30000, 55000)` (supabase/functions/workflow-executor/index.ts),
 * i.e. 55s worst case per node, and real completed runs in okostec-dev (2026-08-14 query) top out
 * at 116s end-to-end (p95 26.7s). 5 minutes is >2.5x that observed max — room for a multi-node
 * chain without false-flagging a run that's merely slow — while still being a small fraction of
 * the 30+ minutes the deduction-based badge was stuck for in the incident this fixes.
 */
export const STALE_RUNNING_MS = 5 * 60 * 1000;

const BAD_TERMINAL_STATUSES = new Set(['failed', 'cancelled', 'timeout']);

export interface ResolveAiAutoStatusParams {
  /** The most recent workflow_run row for this conversation, or null if none exists yet. */
  latestRun: LatestRunForStatus | null;
  /** True if the read against workflow_run itself errored — we couldn't ask the question. */
  runQueryFailed: boolean;
  /** Whether the conversation has any messages at all. */
  hasMessages: boolean;
  /** Whether the newest message was sent by the customer (vs. the agent/AI). */
  lastMessageFromCustomer: boolean;
  /** Injectable for tests; defaults to Date.now(). */
  now?: number;
  /** Injectable for tests; defaults to STALE_RUNNING_MS. */
  staleRunningMs?: number;
}

/**
 * Resolve the AI Auto Mode badge state from real run data. Never returns 'processing' or
 * 'awaitingCustomer' on a guess — those two only come from a run row that says so. Everything the
 * data can't support becomes 'unknown', with a reason attached.
 */
export function resolveAiAutoStatus(params: ResolveAiAutoStatusParams): AiAutoStatusResult {
  const {
    latestRun,
    runQueryFailed,
    hasMessages,
    lastMessageFromCustomer,
    now = Date.now(),
    staleRunningMs = STALE_RUNNING_MS,
  } = params;

  // No messages at all: there is nothing to analyze, full stop — checked before everything else,
  // including runQueryFailed. A conversation with zero messages has nothing to report even if the
  // observability read itself errored.
  //
  // This also guards against a stale workflow_run row surviving a reset: reset_tenant_conversation
  // deliberately does NOT purge workflow_run (see its own migration header,
  // supabase/migrations/20260814120000_add_reset_tenant_conversation_rpc.sql, "public.workflow_run
  // ... confirmed non-blocking ... but also NOT purged by this function"). Without this gate, a
  // 'running' or 'failed' run left over from BEFORE a reset could resurrect "Analyzing Message" (or
  // "Failed") over a conversation that, from the operator's current view, has nothing in it —
  // exactly the report this fixes: reset, then no trace that anything happened, then the stale
  // badge reappearing as if the reset never occurred.
  if (!hasMessages) {
    return { state: 'empty', detail: {} };
  }

  if (runQueryFailed) {
    return { state: 'unknown', detail: { reason: 'queryFailed' } };
  }

  if (latestRun) {
    if (latestRun.status === 'running') {
      const age = now - new Date(latestRun.started_at).getTime();
      if (age <= staleRunningMs) {
        return { state: 'processing', detail: { startedAt: latestRun.started_at } };
      }
      return {
        state: 'unknown',
        detail: { reason: 'staleRunning', startedAt: latestRun.started_at },
      };
    }

    if (BAD_TERMINAL_STATUSES.has(latestRun.status)) {
      return {
        state: 'failed',
        detail: {
          finishedAt: latestRun.finished_at,
          errorSummary: latestRun.error_summary,
        },
      };
    }

    if (latestRun.status === 'succeeded') {
      // A new customer message with no run yet covering it is the same ambiguity as "no run at
      // all" below — say so rather than claiming the (older, already-resolved) run still applies.
      if (lastMessageFromCustomer) {
        return { state: 'unknown', detail: { reason: 'noRunRecorded' } };
      }
      return { state: 'awaitingCustomer', detail: { finishedAt: latestRun.finished_at } };
    }

    // Free-text column (no CHECK... wait, there is one, but an unrecognized value would still
    // land here for forward-compat) — don't invent a meaning for it.
    return { state: 'unknown', detail: {} };
  }

  // No run ever recorded for this conversation. hasMessages is guaranteed true here — the top-of-
  // function gate already returned 'empty' otherwise.
  if (lastMessageFromCustomer) {
    return { state: 'unknown', detail: { reason: 'noRunRecorded' } };
  }
  return { state: 'awaitingCustomer', detail: {} };
}
